import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/styles";
import { useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import AddIcon from "@mui/icons-material/Add";
import Messaging from "bundles/common/components/Messaging/Messaging";
import sh from "bundles/common/utils/sh";
import consumer from "bundles/Candidate/channels/consumer";

const candidateEndpoints = {
  index: "/candidate/conversations",
  search: "/candidate/conversations/search",
  show: (id) => `/candidate/conversations/${id}`,
  message: (id) => `/candidate/conversations/${id}/messages`,
  read: (id) => `/candidate/conversations/${id}/read`,
  block_status: (id) => `/candidate/conversations/${id}/block_status`,
  archive_status: (id) => `/candidate/conversations/${id}/archive_status`,
};

const peerOf = (item) => {
  const company = item?.company || {};
  return { name: company.name, avatar: company.logo_url, subtitle: null };
};

// Picker listing the companies the candidate is eligible to contact, then starts
// (or reopens) the conversation. Rendered as headerExtra so it can drive the
// shared orchestrator via the openConversation helper.
const NewMessageButton = ({ openConversation }) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const [open, setOpen] = useState(false);
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creatingFor, setCreatingFor] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    sh.get("/candidate/messaging_targets")
      .then((res) => setTargets(res.data?.data || []))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, [open]);

  const handlePick = (target) => {
    if (target.conversation_id) {
      setOpen(false);
      openConversation(target.conversation_id);
      return;
    }

    setCreatingFor(target.company.id);
    sh.post("/candidate/conversations", {
      authenticity_token: authenticityToken,
      company_id: target.company.id,
    })
      .then((res) => {
        const id = res.data?.data?.id;
        setOpen(false);
        if (id) openConversation(id);
      })
      .catch(() => {
        enqueueSnackbar(intl.formatMessage({ id: "messaging.error_sending" }), { variant: "error" });
      })
      .finally(() => setCreatingFor(null));
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
      <Button
        variant="rounded"
        color="primaryContained"
        size="small"
        endIcon={<AddIcon fontSize="small" />}
        onClick={() => setOpen(true)}
        sx={{ textTransform: "none", fontFamily: "Segoe UI Semibold, system-ui, sans-serif" }}
      >
        <FormattedMessage id="messaging.new_message" />
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>
          <FormattedMessage id="messaging.new_message_title" />
        </DialogTitle>
        <DialogContent dividers>
          {loading ? null : targets.length === 0 ? (
            <Typography variant="body2" sx={{ color: "text.secondary", py: 2 }}>
              <FormattedMessage id="messaging.new_message_empty" />
            </Typography>
          ) : (
            <List disablePadding>
              {targets.map((target) => (
                <ListItemButton
                  key={target.company.id}
                  onClick={() => handlePick(target)}
                  disabled={creatingFor === target.company.id}
                >
                  <ListItemAvatar>
                    <Avatar src={target.company.logo_url || undefined} alt={target.company.name} />
                  </ListItemAvatar>
                  <ListItemText primary={target.company.name} />
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const Messages = () => {
  const theme = useTheme();
  const candidateId = useSelector((state) => state.current_candidate?.id);

  const emptyState = (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 10, gap: 1.5 }}>
      <Typography variant="h5" sx={{ color: theme.palette.text.main }}>
        <FormattedMessage id="messaging.empty_title" />
      </Typography>
      <Typography
        variant="body1"
        textAlign="center"
        sx={{ color: theme.palette.text.main, maxWidth: "480px" }}
      >
        <FormattedMessage id="messaging.candidate_empty_subtitle" />
      </Typography>
    </Box>
  );

  const config = {
    selfType: "Candidate",
    endpoints: candidateEndpoints,
    consumer,
    actorChannel: "CandidateChannel",
    actorId: candidateId,
    peerOf,
    emptyState,
    headerExtra: ({ openConversation }) => <NewMessageButton openConversation={openConversation} />,
  };

  return (
    <Box sx={{ maxWidth: "1200px", mx: "auto", px: { xs: 1.5, md: 3 }, pb: 6 }}>
      <Messaging config={config} />
    </Box>
  );
};

export default Messages;
